// app/javascript/controllers/dae20/tags_input_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Tags Input — мультиселект в виде чипсов (tags) для virtual_link полей.
 *
 * Позволяет добавлять/удалять значения. При сабмите формы отправляет
 * массив значений через hidden inputs: fields[ID][] = ["val1", "val2"].
 *
 * Targets:
 *   input     — текстовый инпут для ввода новых значений
 *   container — контейнер для чипсов
 *   hiddens   — контейнер для hidden inputs
 */
export default class extends Controller {
  static targets = ["input", "container", "hiddens"]

  static values = {
    fieldName: String,   // name для hidden inputs (fields[2857])
    values: Array        // начальные значения
  }

  connect() {
    this._renderAll()
  }

  // По Enter или по запятой — добавить значение
  onKeydown(event) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      this._addFromInput()
    }
  }

  // По потере фокуса — тоже добавить если что-то введено
  onBlur() {
    this._addFromInput()
  }

  // Кнопка "+" рядом с инпутом
  addTag() {
    this._addFromInput()
  }

  // Удаление тега по клику на ×
  removeTag(event) {
    const value = event.currentTarget.dataset.value
    if (!value) return

    this.valuesValue = this.valuesValue.filter(v => v !== value)
    this._renderAll()
  }

  // ── Private ───────────────────────────────────────────────────────

  _addFromInput() {
    if (!this.hasInputTarget) return

    const raw = this.inputTarget.value.trim()
    if (!raw) return

    // Разбиваем по запятой/точке с запятой/пробелу (для вставки нескольких)
    const newVals = raw.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean)

    const current = new Set(this.valuesValue.map(String))
    let added = false

    for (const val of newVals) {
      if (!current.has(val)) {
        current.add(val)
        added = true
      }
    }

    if (added) {
      this.valuesValue = [...current]
      this._renderAll()
    }

    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  _renderAll() {
    this._renderTags()
    this._renderHiddens()
  }

  _renderTags() {
    if (!this.hasContainerTarget) return

    const values = this.valuesValue || []
    if (values.length === 0) {
      this.containerTarget.innerHTML = ""
      return
    }

    this.containerTarget.innerHTML = values.map(val => `
      <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <span>${this._esc(val)}</span>
        <button type="button"
                class="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-blue-300 transition-colors text-blue-600"
                data-action="click->dae20--tags-input#removeTag"
                data-value="${this._escAttr(val)}"
                title="Удалить">
          <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </span>
    `).join("")
  }

  _renderHiddens() {
    if (!this.hasHiddensTarget) return

    const name = this.fieldNameValue
    const values = this.valuesValue || []

    if (values.length === 0) {
      // Отправляем пустой массив чтобы FormSaver мог очистить связи
      this.hiddensTarget.innerHTML = `<input type="hidden" name="${name}[]" value="">`
      return
    }

    this.hiddensTarget.innerHTML = values.map(val =>
      `<input type="hidden" name="${name}[]" value="${this._escAttr(val)}">`
    ).join("")
  }

  _esc(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  _escAttr(text) {
    return String(text || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
